// Badge — pill-shaped status/tag chip (MASTER.md v3 "Dub" badges): fully
// rounded (9999px), 12px Inter 500, quiet by default (--muted fill + muted
// foreground). Semantic variants keep the app's existing status meanings and
// tint backgrounds in that hue at 10% with the token's text step for ≥4.5:1
// contrast — same pairings the hand-styled chips already use, now centralized.
// `dot` renders the small leading dot the work-type/industry chips carry;
// `color` takes a raw series color for chart-legend chips, which pair the dot
// with a neutral chip so hue stays on the dot only.

import type { HTMLAttributes } from "react";
import { cn } from "@/app/lib/cn";

export type BadgeVariant = "neutral" | "primary" | "success" | "warning" | "danger";

const VARIANTS: Record<BadgeVariant, { chip: string; dot: string }> = {
  // The neutral chip's label is an alpha of --foreground, not
  // --muted-foreground: #737373 on the #f5f5f5 chip measures 4.35:1 in light
  // mode (under AA at 12px). A foreground alpha composites over whatever the
  // chip actually sits on and clears it with room — 7.3:1 light, 8.5:1 dark.
  neutral: { chip: "bg-muted text-foreground/70", dot: "bg-muted-foreground" },
  primary: { chip: "bg-primary/10 text-primary-text", dot: "bg-primary" },
  success: { chip: "bg-success/10 text-success-text", dot: "bg-success" },
  warning: { chip: "bg-warning/10 text-warning-text", dot: "bg-warning" },
  danger: { chip: "bg-destructive/10 text-destructive", dot: "bg-destructive" },
};

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  variant?: BadgeVariant;
  /** Small leading status dot in the variant's hue. */
  dot?: boolean;
  /** Raw series color for the dot (chart legends); the chip stays neutral. */
  color?: string;
  children: React.ReactNode;
}

export function Badge({ variant = "neutral", dot = false, color, className, children, ...rest }: BadgeProps) {
  const style = VARIANTS[variant];

  return (
    <span
      className={cn(
        "inline-flex w-fit flex-none items-center gap-1.5 rounded-pill px-2.5 py-1 text-xs font-medium",
        color ? "bg-muted text-muted-foreground" : style.chip,
        className
      )}
      {...rest}
    >
      {(dot || color) && (
        <span
          className={cn("h-1.5 w-1.5 flex-none rounded-full", !color && style.dot)}
          style={color ? { background: color } : undefined}
        />
      )}
      {children}
    </span>
  );
}
